import { getPaginatedCampaignPopups, revokeCampaignPopup } from '../../../api/campaignPopupApi'
import CampaignPopupFilter from '../../../components/campaign-popups/campaign-popup-filter/campaign-popup-filter'
import LinkButton from '../../../components/link-button/link-button'
import { keysToCamel } from '../../../helpers/keys-to-case'
import { getDateTime, getSearchParams, getSearchValues } from '../../../helpers/utils'
import AuthPermission from '../../../high-order-components/auth-permission'
import translate from '../../../i18n'
import { PageInfo, PermissionsEnum } from '../../../share-types'
import { CampaignPopupStatusOptions, CampaignPopupUserTypeOptions } from '../constant'
import {
  CampaignPopup,
  CampaignPopupStatusEnums,
  CampaignPopupUserTypeEnums,
  ICampaignPopupFilterParams,
  IPaginatedCampaignPopupFilterParams,
  PaginatedCampaignPopup,
} from '../share-type'
import styles from './campaign-popups.module.less'
import { PlusOutlined, DownOutlined } from '@ant-design/icons'
import { Table, Space, Button, Row, Col, Tag, Modal, notification } from 'antd'
import { ColumnsType } from 'antd/lib/table'
import dayjs from 'dayjs'
import { useEffect, useState, MouseEvent } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { compose } from 'redux'

const { confirm } = Modal

const defaultPage = {
  pageSize: 10,
  currentPage: 1,
  count: 0,
}

const CampaignPopups = () => {
  const navigate = useNavigate()
  const [campaignPopups, setCampaignPopups] = useState<CampaignPopup[]>()
  const [pageInfo, setPageInfo] = useState<PageInfo>(defaultPage)
  const [searchValues, setSearchValues] = useState<ICampaignPopupFilterParams>()
  const [searchParams, setSearchParams] = useSearchParams()

  useEffect(() => {
    const newSearchValues = getSearchValues(
      [
        { key: 'summary', isBoolean: false },
        { key: 'startDate', isBoolean: false },
        { key: 'endDate', isBoolean: false },
        { key: 'userType', isBoolean: false },
        { key: 'status', isBoolean: false },
        { key: 'size', isBoolean: false, default: defaultPage.pageSize },
        { key: 'page', isBoolean: false, default: defaultPage.currentPage - 1 },
      ],
      searchParams,
    )

    const data = {
      ...newSearchValues,
      size: Number(newSearchValues.size),
      page: Number(newSearchValues.page),
    }
    setSearchValues(newSearchValues)
    getPaginatedCampaignPopupsAction(data)
  }, [])

  const getPaginatedCampaignPopupsAction = (data: IPaginatedCampaignPopupFilterParams) => {
    getPaginatedCampaignPopups(data).then((res: PaginatedCampaignPopup) => {
      const formatResponse = res.data.map((item: any) => keysToCamel(item))
      setCampaignPopups(formatResponse)
      setPageInfo({
        ...res.pageInfo,
        currentPage: res.pageInfo.currentPage + 1,
      })
    })
  }

  const onSearch = (values: ICampaignPopupFilterParams) => {
    const data = {
      page: 0,
      size: pageInfo.pageSize,
      ...values,
    }
    setSearchValues(values)
    setSearchParams(getSearchParams(values))
    getPaginatedCampaignPopupsAction(data)
  }

  const paginationOnChange = (page: number, pageSize: number) => {
    const data = {
      ...searchValues,
      size: pageSize,
      page: page - 1,
    }

    getPaginatedCampaignPopupsAction(data)
  }

  const onRevokeCampaignPopup = (event: MouseEvent<HTMLElement>, id: number) => {
    event.stopPropagation()
    confirm({
      title: translate('campaignPopups.revokeTitle'),
      onOk: async () => {
        await revokeCampaignPopup(id)
        notification.success({
          message: translate('campaignPopups.revokeSuccess'),
        })
        onSearch({})
      },
    })
  }

  const columns: ColumnsType<CampaignPopup> = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      sorter: (a, b) => a.id - b.id,
      width: 50,
    },
    {
      title: translate('campaignPopups.summary'),
      dataIndex: 'summary',
      key: 'summary',
    },
    {
      title: translate('campaignPopups.startDate'),
      dataIndex: 'startDate',
      key: 'startDate',
      render: (startDate: string) => {
        return <span>{getDateTime(startDate)}</span>
      },
      sorter: (a, b) => {
        if (dayjs(a.startDate).isBefore(b.startDate)) {
          return 1
        }
        return 0
      },
    },
    {
      title: translate('campaignPopups.endDate'),
      dataIndex: 'endDate',
      key: 'endDate',
      render: (endDate: string) => {
        return <span>{getDateTime(endDate)}</span>
      },
      sorter: (a, b) => {
        if (dayjs(a.endDate).isBefore(b.endDate)) {
          return 1
        }
        return 0
      },
    },
    {
      title: translate('campaignPopups.userType'),
      dataIndex: 'userType',
      key: 'userType',
      render: (userType: CampaignPopupUserTypeEnums) => {
        const tag = CampaignPopupUserTypeOptions.find(item => item.value === userType)?.label
        return <Tag>{tag}</Tag>
      },
      filters: CampaignPopupUserTypeOptions,
      filterIcon: <DownOutlined />,
      onFilter: (value, record) => record.userType === value,
    },
    {
      title: translate('campaignPopups.status'),
      dataIndex: 'status',
      key: 'status',
      render: (status: CampaignPopupStatusEnums) => {
        const tag = CampaignPopupStatusOptions.find(item => item.value === status)?.label
        return (
          <Tag color={status === CampaignPopupStatusEnums.INACTIVE ? 'red' : 'green'}>{tag}</Tag>
        )
      },
      filters: CampaignPopupStatusOptions,
      filterIcon: <DownOutlined />,
      onFilter: (value, record) => record.status === value,
    },
    {
      title: translate('tableActionColumn'),
      key: 'action',
      render: (text: string, record: any) => (
        <Space size="middle">
          <AuthPermission permission={PermissionsEnum.CAMPAIGN_POPUP_WRITE}>
            <LinkButton
              to={`/campaign-popups/${record.id}/edit`}
              disabled={record.status === CampaignPopupStatusEnums.INACTIVE}
            >
              {translate('edit')}
            </LinkButton>
          </AuthPermission>

          <LinkButton to={`/campaign-popups/${record.id}`}>{translate('view')}</LinkButton>

          <AuthPermission permission={PermissionsEnum.CAMPAIGN_POPUP_WRITE}>
            <Button
              danger
              type="link"
              block
              disabled={record.status === CampaignPopupStatusEnums.INACTIVE}
              onClick={event => onRevokeCampaignPopup(event, record.id)}
            >
              {translate('revoke')}
            </Button>
          </AuthPermission>
        </Space>
      ),
    },
  ]

  return (
    <Space className={styles['campaign-popup-list-space']} orientation="vertical" size={16}>
      <Row justify="center" align="middle">
        <Col xs={{ span: 24 }} sm={{ span: 12 }} className={styles['left-col']}>
          <div>{translate('campaignPopups.listTitle')}</div>
        </Col>
        <Col xs={{ span: 24 }} sm={{ span: 12 }} className={styles['right-col']}>
          <AuthPermission permission={PermissionsEnum.CAMPAIGN_POPUP_WRITE}>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => navigate('/campaign-popups/new')}
            >
              {translate('campaignPopups.createTitle')}
            </Button>
          </AuthPermission>
        </Col>
      </Row>

      <div className={styles['filter-bar']}>
        <CampaignPopupFilter onSearch={onSearch} formData={searchValues} />
      </div>

      <Table
        rowKey={record => record.id}
        columns={columns}
        dataSource={campaignPopups}
        pagination={{
          current: pageInfo.currentPage,
          pageSize: pageInfo.pageSize,
          total: pageInfo.count,
          onChange: paginationOnChange,
        }}
      />
    </Space>
  )
}

export default compose()(CampaignPopups)
